import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Skeleton,
  useTheme,
  Tooltip,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
} from '@mui/icons-material';

/**
 * Enterprise-grade MetricCard component
 * Displays key metrics with trend indicators
 *
 * @param {Object} props
 * @param {string} props.title - Metric title (shown in tooltip)
 * @param {string|number} props.value - Primary metric value
 * @param {string} props.unit - Unit of measurement
 * @param {string} props.subtitle - Additional context
 * @param {number} props.change - Percentage change
 * @param {string} props.trend - Trend direction: 'up', 'down', 'flat'
 * @param {boolean} props.lowerIsBetter - Colour a fall green and a rise red (error rate, load time)
 * @param {React.ReactNode} props.icon - Custom icon
 * @param {string} props.iconColor - Color for the icon (e.g., 'primary.main', '#ff0000')
 * @param {boolean} props.loading - Loading state
 * @param {Function} props.onClick - Click handler
 * @param {string} props.color - Custom color for value
 * @param {boolean} props.showTitle - Whether to show title text above the value.
 *   Default changed to true so the label "CPU Load" renders above "4%" rather
 *   than being hidden in a hover tooltip. Pass {showTitle={false}} to opt
 *   back into the old compact (icon + number only) rendering.
 */
const MetricCard = ({
  title,
  value,
  unit,
  subtitle,
  change,
  trend,
  icon,
  iconColor,
  loading = false,
  onClick,
  color,
  showTitle = true,
  lowerIsBetter = false,
}) => {
  const theme = useTheme();

  // Sanitize change: treat NaN, Infinity, and non-finite values as undefined
  const safeChange = (typeof change === 'number' && isFinite(change)) ? change : undefined;

  const getTrendConfig = () => {
    if (trend === 'up' || (safeChange && safeChange > 0)) {
      return {
        icon: <TrendingUpIcon fontSize="small" />,
        color: lowerIsBetter ? theme.palette.error.main : theme.palette.success.main,
      };
    } else if (trend === 'down' || (safeChange && safeChange < 0)) {
      return {
        icon: <TrendingDownIcon fontSize="small" />,
        color: lowerIsBetter ? theme.palette.success.main : theme.palette.error.main,
      };
    } else {
      return {
        icon: <TrendingFlatIcon fontSize="small" />,
        color: theme.palette.text.secondary,
      };
    }
  };

  const trendConfig = getTrendConfig();

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Skeleton variant="text" width="60%" height={20} />
          <Skeleton variant="text" width="80%" height={40} sx={{ my: 1 }} />
          <Skeleton variant="text" width="40%" height={16} />
        </CardContent>
      </Card>
    );
  }

  // Cursor + hover lift only when the card actually does something on click.
  // Without this gate every page's KPI tiles felt clickable and lifted on
  // hover even when there was no destination — confusing affordance and not
  // visually consistent with a static metric tile.
  const interactive = typeof onClick === 'function';

  const cardContent = (
    <Card
      sx={{
        height: '100%',                      // uniform tile height inside a Grid row
        ...(interactive && {
          cursor: 'pointer',
          transition: 'all 0.2s',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: theme.shadows[2],
          },
        }),
      }}
      onClick={onClick}
    >
      <CardContent sx={{ py: 0.75, px: 1.5, '&:last-child': { pb: 0.75 } }}>
        {showTitle && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              fontWeight: 500,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              mb: 0.5,
            }}
          >
            {title}
          </Typography>
        )}

        {/* Icon and Value side by side */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: showTitle ? 'space-between' : 'flex-start',
            gap: 1,
          }}
        >
          {icon && (
            <Box
              sx={{
                color: iconColor || 'text.secondary',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '& > svg': {
                  fontSize: showTitle ? 16 : 20,
                },
              }}
            >
              {icon}
            </Box>
          )}

          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
            <Typography
              variant="h6"
              component="div"
              sx={{
                fontWeight: 600,
                fontSize: '1.125rem',
                lineHeight: 1.2,
                color: color || 'text.primary',
              }}
            >
              {value}
            </Typography>
            {unit && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 500, fontSize: '0.7rem' }}
              >
                {unit}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Subtitle and trend */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
          {(safeChange !== undefined || trend) && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                color: trendConfig.color,
              }}
            >
              {trendConfig.icon}
              {safeChange !== undefined && (
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: trendConfig.color,
                  }}
                >
                  {Math.abs(safeChange).toFixed(1)}%
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Tooltip title={title} arrow placement="top" enterDelay={300}>
      {cardContent}
    </Tooltip>
  );
};

export default MetricCard;
